import React from 'react';
import { Lightbulb, HelpCircle, ListChecks, Monitor, BookOpen } from 'lucide-react';
import type { FC } from 'react';
import type { LessonSection } from '@/lib/learning/courses';

const SECTION_CONFIG: Record<
  LessonSection['type'],
  { icon: FC<{ className?: string }>; color: string; bg: string; border: string; accent: string }
> = {
  concept: {
    icon: Lightbulb,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800/50',
    accent: 'bg-amber-500 dark:bg-amber-400',
  },
  why: {
    icon: HelpCircle,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800/50',
    accent: 'bg-blue-500 dark:bg-blue-400',
  },
  methodology: {
    icon: ListChecks,
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800/50',
    accent: 'bg-emerald-500 dark:bg-emerald-400',
  },
  'in-product': {
    icon: Monitor,
    color: 'text-violet-600 dark:text-violet-400',
    bg: 'bg-violet-50 dark:bg-violet-900/20',
    border: 'border-violet-200 dark:border-violet-800/50',
    accent: 'bg-violet-500 dark:bg-violet-400',
  },
  further: {
    icon: BookOpen,
    color: 'text-slate-600 dark:text-slate-400',
    bg: 'bg-slate-50 dark:bg-slate-800',
    border: 'border-slate-200 dark:border-slate-700',
    accent: 'bg-slate-500 dark:bg-slate-400',
  },
};

/* ------------------------------------------------------------------ */
/*  Content block types produced by the parser                        */
/* ------------------------------------------------------------------ */

interface ParagraphBlock {
  kind: 'paragraph';
  text: string;
}

interface OrderedListBlock {
  kind: 'ol';
  items: { number: string; text: string }[];
}

interface UnorderedListBlock {
  kind: 'ul';
  items: string[];
}

type ContentBlock = ParagraphBlock | OrderedListBlock | UnorderedListBlock;

/* ------------------------------------------------------------------ */
/*  Parser — transforms raw markdown-ish content into typed blocks    */
/* ------------------------------------------------------------------ */

const OL_RE = /^(\d+)\.\s+(.+)$/;
const UL_RE = /^-\s+(.+)$/;

function parseContent(raw: string): ContentBlock[] {
  const paragraphs = raw.split('\n\n');
  const blocks: ContentBlock[] = [];

  for (const para of paragraphs) {
    const lines = para.split('\n');

    // Check if every line is an ordered list item
    const olMatches = lines.map((l) => OL_RE.exec(l.trim()));
    if (lines.length > 1 && olMatches.every(Boolean)) {
      blocks.push({
        kind: 'ol',
        items: olMatches.map((m) => ({ number: m![1], text: m![2] })),
      });
      continue;
    }

    // Check if every line is an unordered list item
    const ulMatches = lines.map((l) => UL_RE.exec(l.trim()));
    if (lines.length > 1 && ulMatches.every(Boolean)) {
      blocks.push({
        kind: 'ul',
        items: ulMatches.map((m) => m![1]),
      });
      continue;
    }

    // Mixed: paragraph may contain an intro line followed by list items
    // e.g.  "Le SEO repose sur 3 piliers :\n- **Technique** — …\n- **Contenu** — …"
    const firstNonList = lines.findIndex(
      (l) => !OL_RE.test(l.trim()) && !UL_RE.test(l.trim()),
    );
    const firstList = lines.findIndex(
      (l) => OL_RE.test(l.trim()) || UL_RE.test(l.trim()),
    );

    if (firstList > 0 && firstNonList < firstList) {
      // Intro paragraph + list
      const introLines = lines.slice(0, firstList);
      const listLines = lines.slice(firstList);

      blocks.push({ kind: 'paragraph', text: introLines.join('\n') });

      const olItems = listLines.map((l) => OL_RE.exec(l.trim()));
      if (olItems.every(Boolean)) {
        blocks.push({
          kind: 'ol',
          items: olItems.map((m) => ({ number: m![1], text: m![2] })),
        });
      } else {
        const ulItems = listLines.map((l) => UL_RE.exec(l.trim()));
        if (ulItems.every(Boolean)) {
          blocks.push({ kind: 'ul', items: ulItems.map((m) => m![1]) });
        } else {
          // Fallback: treat remaining as paragraph
          blocks.push({ kind: 'paragraph', text: listLines.join('\n') });
        }
      }
      continue;
    }

    // Plain paragraph (may contain single \n — join with space)
    blocks.push({ kind: 'paragraph', text: lines.join(' ') });
  }

  return blocks;
}

/* ------------------------------------------------------------------ */
/*  Inline rendering — bold + regular text                            */
/* ------------------------------------------------------------------ */

function renderInline(text: string): (string | React.ReactElement)[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-slate-900 dark:text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
}

/* ------------------------------------------------------------------ */
/*  Block renderers                                                   */
/* ------------------------------------------------------------------ */

const ParagraphRenderer: FC<{ text: string }> = ({ text }) => (
  <p>{renderInline(text)}</p>
);

const OrderedListRenderer: FC<{ items: { number: string; text: string }[]; accent: string }> = ({
  items,
  accent,
}) => (
  <ol className="space-y-2.5 my-1">
    {items.map((item, idx) => (
      <li key={idx} className="flex items-start gap-3">
        <span
          className={`${accent} flex-shrink-0 mt-0.5 flex h-5.5 w-5.5 items-center justify-center rounded-full text-[11px] font-bold text-white`}
          style={{ minWidth: '1.375rem', minHeight: '1.375rem', width: '1.375rem', height: '1.375rem' }}
        >
          {item.number}
        </span>
        <span className="leading-relaxed flex-1">{renderInline(item.text)}</span>
      </li>
    ))}
  </ol>
);

const UnorderedListRenderer: FC<{ items: string[]; accent: string }> = ({ items, accent }) => (
  <ul className="space-y-2 my-1">
    {items.map((item, idx) => (
      <li key={idx} className="flex items-start gap-3">
        <span
          className={`${accent} flex-shrink-0 mt-2 rounded-full`}
          style={{ minWidth: '0.375rem', minHeight: '0.375rem', width: '0.375rem', height: '0.375rem' }}
        />
        <span className="leading-relaxed flex-1">{renderInline(item)}</span>
      </li>
    ))}
  </ul>
);

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */

interface LessonContentProps {
  sections: LessonSection[];
}

/**
 * Rendu structure du contenu d'une lecon — chaque section a une icone,
 * couleur propre, et le contenu est parse pour rendre les listes joliment.
 */
export const LessonContent: FC<LessonContentProps> = ({ sections }) => {
  return (
    <div className="space-y-5">
      {sections.map((section) => {
        const cfg = SECTION_CONFIG[section.type] ?? SECTION_CONFIG.concept;
        const SIcon = cfg.icon;
        const blocks = parseContent(section.content);

        return (
          <div
            key={section.type}
            className={`rounded-xl border ${cfg.border} ${cfg.bg} p-5 transition-colors duration-200`}
          >
            {/* Section header */}
            <div className="flex items-center gap-2.5 mb-3">
              <SIcon className={`w-4.5 h-4.5 ${cfg.color} flex-shrink-0`} />
              <h3 className={`text-sm font-semibold ${cfg.color}`}>{section.title}</h3>
            </div>

            {/* Content blocks */}
            <div className="text-sm leading-relaxed text-slate-700 dark:text-slate-300 space-y-3">
              {blocks.map((block, bIdx) => {
                switch (block.kind) {
                  case 'ol':
                    return (
                      <OrderedListRenderer key={bIdx} items={block.items} accent={cfg.accent} />
                    );
                  case 'ul':
                    return (
                      <UnorderedListRenderer key={bIdx} items={block.items} accent={cfg.accent} />
                    );
                  case 'paragraph':
                    return <ParagraphRenderer key={bIdx} text={block.text} />;
                }
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
